import { ActivityIndicator, View } from 'react-native';
import Animated, { FadeIn, FadeOut } from 'react-native-reanimated';
import { observer } from 'mobx-react-lite';
import TextLato from '@photosi/rn-components/text/text-lato/TextLato';
import { Icon } from '@photosi/rn-components/icon/Icon';
import { Tooltip } from '@photosi/rn-components/tooltip/Tooltip';
import { BaseButton } from '@photosi/rn-components/buttons/base-button/BaseButton';
import { TooltipSaveProject } from '@photosi/rn-components/tooltip-save-project/TooltipSaveProject';
import { TooltipPosition } from '@photosi/rn-components/tooltips/Tooltip.types';
import { DRAWER_POSITIONS, DRAWER_VIEWS } from 'src/store/drawer/drawer.constants';
import { TestIDs } from 'src/constants/testIDs.constants';
import { SAVING_STATE } from 'src/store/page-project/pageProject.constants';
import { icon } from 'src/theme/colors';
import { useStore } from 'src/contexts/StoreContext';
import { SaveButtonProps } from '../SaveButton.types';
import { ICON_NAMES } from '@photosi/rn-components/icon/icon.helper';
import { useSaveButtonDesktopLogic } from './SaveButtonDesktop.logic';
import { styles } from './SaveButtonDesktop.styles';

export const SaveButtonDesktop = observer(({ focusId, focusOrder, focusGroup }: SaveButtonProps) => {
  const {
    i18n,
    project: { activeProject, isUploading },
    drawer: { openDrawer },
    user: { isLogged: isUserLogged },
  } = useStore();
  const { saveButtonText, saveButtonTextVisible } = useSaveButtonDesktopLogic();

  let rendererFunction = () => (
    <View style={styles.leftAction}>
      <View style={styles.leftActionIcon}>
        {isUserLogged && (activeProject!.savingState === SAVING_STATE.saving || isUploading) ? (
          <ActivityIndicator size={20} color={icon.iconInteractive} />
        ) : !isUserLogged || saveButtonTextVisible || activeProject!.savingState === SAVING_STATE.saved ? (
          <Icon name={ICON_NAMES.Saved} color={icon.iconInteractive} />
        ) : null}
      </View>

      {!isUserLogged ? (
        <TextLato style={styles.leftActionText}>{i18n.t('header:save')}</TextLato>
      ) : saveButtonTextVisible || isUploading ? (
        <Animated.View entering={FadeIn.duration(300)} exiting={FadeOut.duration(300)}>
          <TextLato style={styles.leftActionText}>
            {!isUploading ? saveButtonText : i18n.t('header:uploading')}
          </TextLato>
        </Animated.View>
      ) : null}
    </View>
  );

  if (!isUserLogged) {
    return (
      <BaseButton
        onPress={() => {
          openDrawer({
            drawerView: DRAWER_VIEWS.Login,
            drawerPosition: DRAWER_POSITIONS.Right,
          });
        }}
        focusId={focusId}
        focusOrder={focusOrder}
        focusGroup={focusGroup}
        accessible={true}
        accessibilityLabel={i18n.t('header:save')}
        accessibilityRole="button"
      >
        {rendererFunction}
      </BaseButton>
    );
  } else {
    return (
      <Tooltip
        TooltipContent={TooltipSaveProject}
        position={TooltipPosition.BOTTOM}
        tooltipID={TestIDs.TOOLTIP_SAVE_PROJECT}
        focusId={focusId}
        focusOrder={focusOrder}
        focusGroup={focusGroup}
        accessible={true}
        accessibilityLabel={!isUploading ? saveButtonText : i18n.t('header:uploading')}
        accessibilityRole="button"
      >
        {rendererFunction}
      </Tooltip>
    );
  }
});
